import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert
} from '@mui/material';
import { Repeat, Schedule, NotificationsActive } from '@mui/icons-material';

interface RecurringTaskFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RecurringTaskData) => Promise<void>;
}

interface RecurringTaskData {
  title: string;
  description: string;
  scheduledHour: number;
  label: string;
  priority: number;
  recurrenceType: 'daily' | 'weekly' | 'monthly' | 'custom';
  recurrenceDays: number[];
  recurrenceDayOfMonth: number;
  recurrenceInterval: number;
  reminderEnabled: boolean;
  reminderMinutesBefore: number;
}

const DAYS_OF_WEEK = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i === 0 ? 12 : i > 12 ? i - 12 : i}:00 ${i >= 12 ? 'PM' : 'AM'}`
}));

const REMINDER_OPTIONS = [
  { value: 15, label: '15 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 120, label: '2 hours before' },
  { value: 1440, label: '1 day before' },
];

const RecurringTaskForm: React.FC<RecurringTaskFormProps> = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<RecurringTaskData>({
    title: '',
    description: '',
    scheduledHour: 9,
    label: 'personal',
    priority: 3,
    recurrenceType: 'daily',
    recurrenceDays: [1, 2, 3, 4, 5], // Mon-Fri default
    recurrenceDayOfMonth: 1,
    recurrenceInterval: 1,
    reminderEnabled: true,
    reminderMinutesBefore: 60
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field: keyof RecurringTaskData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleDayToggle = (day: number) => {
    setFormData(prev => {
      const days = prev.recurrenceDays.includes(day)
        ? prev.recurrenceDays.filter(d => d !== day)
        : [...prev.recurrenceDays, day].sort();
      return { ...prev, recurrenceDays: days };
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    if (formData.recurrenceType === 'weekly' && formData.recurrenceDays.length === 0) {
      setError('Please select at least one day for weekly recurrence');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onSubmit(formData);
      onClose();
      // Reset form
      setFormData({
        title: '',
        description: '',
        scheduledHour: 9,
        label: 'personal',
        priority: 3,
        recurrenceType: 'daily',
        recurrenceDays: [1, 2, 3, 4, 5],
        recurrenceDayOfMonth: 1,
        recurrenceInterval: 1,
        reminderEnabled: true,
        reminderMinutesBefore: 60
      });
    } catch (err: any) {
      setError(err.message || 'Failed to create recurring task');
    } finally {
      setLoading(false);
    }
  };

  const getPatternDescription = () => {
    switch (formData.recurrenceType) {
      case 'daily':
        return 'Every day';
      case 'weekly':
        const days = formData.recurrenceDays.map(d => DAYS_OF_WEEK.find(dw => dw.value === d)?.label).join(', ');
        return `Every ${days}`;
      case 'monthly':
        return `Every month on day ${formData.recurrenceDayOfMonth}`;
      case 'custom':
        return `Every ${formData.recurrenceInterval} day${formData.recurrenceInterval > 1 ? 's' : ''}`;
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Repeat color="primary" />
        Create Recurring Task
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Task Details */}
          <TextField
            label="Task Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            required
            placeholder="e.g., Daily Standup, Weekly Review"
          />

          <TextField
            label="Description (optional)"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            fullWidth
            multiline
            rows={2}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Time</InputLabel>
              <Select
                value={formData.scheduledHour}
                label="Time"
                onChange={(e) => handleChange('scheduledHour', e.target.value)}
              >
                {HOURS.map(h => (
                  <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Label</InputLabel>
              <Select
                value={formData.label}
                label="Label"
                onChange={(e) => handleChange('label', e.target.value)}
              >
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="office">Office</MenuItem>
                <MenuItem value="health">Health</MenuItem>
                <MenuItem value="learning">Learning</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleChange('priority', e.target.value)}
              >
                <MenuItem value={1}>Urgent</MenuItem>
                <MenuItem value={2}>High</MenuItem>
                <MenuItem value={3}>Normal</MenuItem>
                <MenuItem value={4}>Low</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Recurrence Pattern */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            Recurrence Pattern
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Repeat</InputLabel>
            <Select
              value={formData.recurrenceType}
              label="Repeat"
              onChange={(e) => handleChange('recurrenceType', e.target.value)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="custom">Custom Interval</MenuItem>
            </Select>
          </FormControl>

          {/* Weekly - Day Selection */}
          {formData.recurrenceType === 'weekly' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select days:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {DAYS_OF_WEEK.map(day => (
                  <Chip
                    key={day.value}
                    label={day.label}
                    onClick={() => handleDayToggle(day.value)}
                    color={formData.recurrenceDays.includes(day.value) ? 'primary' : 'default'}
                    variant={formData.recurrenceDays.includes(day.value) ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Monthly - Day of Month */}
          {formData.recurrenceType === 'monthly' && (
            <FormControl fullWidth>
              <InputLabel>Day of Month</InputLabel>
              <Select
                value={formData.recurrenceDayOfMonth}
                label="Day of Month"
                onChange={(e) => handleChange('recurrenceDayOfMonth', e.target.value)}
              >
                {Array.from({ length: 31 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Custom - Interval */}
          {formData.recurrenceType === 'custom' && (
            <TextField
              label="Repeat every X days"
              type="number"
              value={formData.recurrenceInterval}
              onChange={(e) => handleChange('recurrenceInterval', Math.max(1, parseInt(e.target.value) || 1))}
              fullWidth
              inputProps={{ min: 1, max: 365 }}
            />
          )}

          {/* Pattern Preview */}
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              {getPatternDescription()} at {HOURS.find(h => h.value === formData.scheduledHour)?.label}
            </Typography>
          </Box>

          {/* Reminder Settings */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            <NotificationsActive sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
            Reminder Settings
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.reminderEnabled}
                onChange={(e) => handleChange('reminderEnabled', e.target.checked)}
              />
            }
            label="Enable reminder for this task"
          />

          {formData.reminderEnabled && (
            <FormControl fullWidth>
              <InputLabel>Remind me</InputLabel>
              <Select
                value={formData.reminderMinutesBefore}
                label="Remind me"
                onChange={(e) => handleChange('reminderMinutesBefore', e.target.value)}
              >
                {REMINDER_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={<Repeat />}
        >
          {loading ? 'Creating...' : 'Create Recurring Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurringTaskForm;
